import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, Image as ImageIcon, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

/** Profile → PDF template picker with live preview + optional logo (brief §5.6). */
export function InvoiceTemplateCard({ api, profile, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [current, setCurrent] = useState(profile?.invoice_template || 'simple');
  const [previewKey, setPreviewKey] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.get('/portal/invoice-templates').then(r => { setTemplates(r.data.templates || []); setCurrent(r.data.current); }).catch(() => {}); }, [api]);

  const loadLogo = useCallback(async () => {
    if (!profile?.has_logo) { setLogoUrl(null); return; }
    try {
      const r = await api.get('/portal/invoice-logo', { responseType: 'blob' });
      setLogoUrl(URL.createObjectURL(r.data));
    } catch { setLogoUrl(null); }
  }, [api, profile?.has_logo]);
  useEffect(() => { loadLogo(); }, [loadLogo]);

  const openPreview = async (key) => {
    setPreviewKey(key); setPreviewUrl(null);
    try {
      const r = await api.get(`/portal/invoice-templates/${key}/preview.png`, { responseType: 'blob' });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch { toast.error('Could not render the preview'); setPreviewKey(null); }
  };

  const choose = async (key) => {
    setBusy(true);
    try {
      const r = await api.put('/portal/contractor-profile', { invoice_template: key });
      setCurrent(key); toast.success(`Template set to ${key}`); onSaved?.(r.data); setPreviewKey(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/portal/invoice-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded');
      const r = await api.get('/portal/contractor-profile'); onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const removeLogo = async () => {
    setBusy(true);
    try { await api.delete('/portal/invoice-logo'); toast.success('Logo removed'); const r = await api.get('/portal/contractor-profile'); onSaved?.(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="invoice-template-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-sky-300" /> Invoice PDF template</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2" data-testid="template-options">
          {templates.map(t => (
            <div key={t.key} className={`rounded-xl border p-3 flex items-center gap-3 ${current === t.key ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/15 bg-[#1E2530]'}`} data-testid={`template-option-${t.key}`}>
              <button type="button" onClick={() => choose(t.key)} disabled={busy} className="flex-1 text-left min-h-[44px]" data-testid={`template-choose-${t.key}`}>
                <div className="text-white font-semibold flex items-center gap-2">{t.name}{current === t.key && <span className="text-[10px] uppercase tracking-wider text-[#F5A623]">in use</span>}</div>
                <div className="text-xs text-gray-400">{t.blurb}</div>
              </button>
              <button type="button" onClick={() => openPreview(t.key)} className="w-11 h-11 rounded-lg bg-white/5 text-gray-300 flex items-center justify-center active:scale-95" title="Preview" data-testid={`template-preview-${t.key}`}><Eye className="w-5 h-5" /></button>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-white/15 bg-[#1E2530] p-3">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="invoice-logo-img" /> : <ImageIcon className="w-6 h-6 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold">Logo (optional)</div>
              <div className="text-xs text-gray-400">Shown on the Branded template. PNG or JPEG, under 2MB.</div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} data-testid="invoice-logo-input" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="border-white/20 text-white bg-white/5 min-h-[44px]" data-testid="invoice-logo-upload-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (logoUrl ? 'Replace' : 'Upload')}</Button>
            {logoUrl && <button type="button" onClick={removeLogo} className="w-11 h-11 rounded-lg text-gray-400 hover:text-red-400 flex items-center justify-center" title="Remove logo" data-testid="invoice-logo-remove-btn"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>
      </CardContent>

      <Dialog open={!!previewKey} onOpenChange={(o) => { if (!o) setPreviewKey(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-[#1E2530] border-white/10 text-white p-0 w-[96vw] max-w-[430px] h-[92vh] flex flex-col overflow-hidden" data-testid="template-preview-dialog">
          <div className="px-4 py-3 border-b border-white/10 pr-12"><DialogTitle className="text-base">Preview — {templates.find(t => t.key === previewKey)?.name}</DialogTitle></div>
          <div className="flex-1 min-h-0 overflow-auto bg-[#0F141B] p-2">
            {previewUrl ? <img src={previewUrl} alt="Template preview" className="w-full bg-white shadow-2xl" data-testid="template-preview-img" /> : <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
          </div>
          <div className="p-3 border-t border-white/10">
            <Button onClick={() => choose(previewKey)} disabled={busy || current === previewKey} className="w-full h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="template-use-btn">{current === previewKey ? 'Already in use' : 'Use this template'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
